/**
 * Country Manager Module
 * Handles country list, selection, and map view changes
 */

import { fetchDefaultMap } from '../config/firebase';
import { selectedLanguage } from '../utils/translation';
import { getMap, getControlAreaLayers, getAllEventsLayers, getSidebar } from './mapConfig';
import { fetchAllCSVData } from './eventManager';
import { loadControlAreas } from './conflictZoneManager';

export let selectedCountry = null;

/**
 * Get country flag emoji
 */
function getCountryFlag(countryName) {
    const flags = {
        'Syria': '🇸🇾',
        'Palestine': '🇵🇸',
        'Israel': '🇮🇱',
        'Lebanon': '🇱🇧',
        'Iraq': '🇮🇶',
        'Yemen': '🇾🇪',
        'Turkey': '🇹🇷'
    };
    return flags[countryName] || '🌍';
}

/**
 * Add a country to the sidebar grid and navbar
 */
function addCountry(countryName, translatedCountryName) {
    const countriesGrid = document.querySelector("#countries-grid");
    const countryNavList = document.querySelector("#nav-country-list");

    // Create country card for sidebar grid
    const countryCard = document.createElement("div");
    countryCard.className = 'country-card';
    countryCard.setAttribute('data-country', countryName);
    // for flags use -> <div class="country-flag">${getCountryFlag(countryName)}</div>

    countryCard.innerHTML = `
        <div class="country-name">${translatedCountryName}</div>
        <div class="country-status status-conflict">
            <i class="fa fa-circle"></i>
            <span>Conflict Zone</span>
        </div>
    `;

    // Create navbar list item
    const listItemNav = document.createElement("li");
    listItemNav.className = 'nav-item custom-navlist-class';
    listItemNav.innerHTML = `<a class='nav-link' href="#${translatedCountryName}">${translatedCountryName}</a>`;

    // Add click event listeners
    countryCard.addEventListener("click", async () => {
        // Remove active class from all cards
        document.querySelectorAll('.country-card').forEach(card => {
            card.classList.remove('active');
        });
        // Add active class to clicked card
        countryCard.classList.add('active');
        await changeMapView(countryName);
    });

    listItemNav.addEventListener("click", async () => {
        await changeMapView(countryName);
    });

    // Append to containers
    countriesGrid.appendChild(countryCard);
    if (countryNavList) {
        countryNavList.appendChild(listItemNav);
    }
}

/**
 * Change map view to selected country
 */
async function changeMapView(countryName) {
    console.log("Changing map to", countryName);
    
    const map = getMap();
    const controlAreaLayers = getControlAreaLayers();
    const allEventsLayers = getAllEventsLayers();
    const sidebar = getSidebar();
    
    if (!map) {
        console.error('Map not initialized!');
        return;
    }
    
    const countryData = await fetchDefaultMap();
    const country = Object.values(countryData).find((c) => c.name === countryName);

    if (!country) {
        console.log("Country not found");
        return;
    }

    // Clear all layers on the map
    if (controlAreaLayers) {
        controlAreaLayers.clearLayers();
    }
    if (allEventsLayers) {
        allEventsLayers.clearLayers();
    }

    // Collapse sidebar when country clicked
    if (sidebar) {
        sidebar.close();
    }

    // Set map view
    map.setView([country.latitude, country.longitude], country.zoomLevel);

    // Load country-specific data
    switch (country.name) {
        case "Syria":
            loadControlAreas(country.name);
            await fetchAllCSVData(country.name);
            selectedCountry = country.name;
            break;
        case "Palestine":
            loadControlAreas("Israel");
            await fetchAllCSVData(country.name);
            selectedCountry = country.name;
            break;
        case "Israel":
            await fetchAllCSVData(country.name);
            selectedCountry = country.name;
            break;
    }
}

/**
 * Process and add all countries to the list
 */
export async function processCountries() {
    console.log('Starting processCountries...');
    const countryData = await fetchDefaultMap();
    console.log('Country data fetched:', countryData);
    const defaultMap = Object.values(countryData);

    for (const country of defaultMap) {
        const languageMap = {
            "en": country.name,
            "ar": country.name_ar,
            "tr": country.name_tr,
        };
        const translatedCountryName = languageMap[selectedLanguage] || country.name;
        addCountry(country.name, translatedCountryName);
        
        if (country.priority) {
            console.log('Found priority country:', country.name);
            await setDefaultMap(country);
        }
    }
    console.log('Finished processCountries');
}

/**
 * Set default map view
 */
async function setDefaultMap(priorityCountry) {
    if (priorityCountry.priority) {
        const map = getMap();
        
        if (!map) {
            console.error('Map not initialized in setDefaultMap!');
            return;
        }
        
        console.log('Setting default map for:', priorityCountry.name, priorityCountry.zoomLevel);
        map.setView([priorityCountry.latitude, priorityCountry.longitude], priorityCountry.zoomLevel);
        loadControlAreas(priorityCountry.name);
        await fetchAllCSVData(priorityCountry.name);
        selectedCountry = priorityCountry.name;
    }
}
